import { createElement } from "react"

import * as Sentry from "@sentry/react"
import toast from "react-hot-toast"
import { Button } from "semantic-ui-react"
import { API, asMutation, asQuery } from "simplydo/core/api"
import { WebsocketAPI } from "simplydo/core/api/websocket"
import { getLocalStorageItem, removeLocalStorageItem } from "simplydo/core/helpers"
import { i18n } from "simplydo/core/i18n"
import SearchParams from "simplydo/core/searchParams"

import actions from "actions"
import { store } from "store"
import util from "utils/utils"

export const websocketApi = new WebsocketAPI({
  generateToken: () =>
    new Promise((resolve) =>
      api.auth.requestWebsocketToken(
        ({ token: newWebsocketToken }) => resolve(newWebsocketToken),
        () => resolve(null),
      ),
    ),
})

const onLogout = () => {
  removeLocalStorageItem("auth_token")
  // @ts-ignore
  window.$chatwoot?.reset()
  websocketApi.disconnect()
  api.token = null
  store.dispatch(actions.messages.logout())
  store.dispatch(actions.user.logout())
  store.dispatch(actions.auth.logout())
}

const api = new API({
  onBeforeRequest: () => {
    const now = new Date()
    const { user } = store.getState()
    if (
      user?._id &&
      util.localStorageIsSupported() &&
      now.getTime() - new Date(localStorage.getItem("heartbeat")).getTime() > 3600000
    ) {
      localStorage.setItem("heartbeat", now.toString())
      api.users.heartbeat(
        user._id,
        () => {},
        () => {},
      )
    }
  },
  onLogin: (success, fail) => {
    if (!api.token) {
      const token = getLocalStorageItem("auth_token")
      if (token) {
        api.token = token
      } else {
        fail?.()
        return
      }
    }

    store.dispatch(actions.entry.clear())
    store.dispatch(actions.auth.login())
    store.dispatch(actions.auth.startGettingUser())

    const storeState = store.getState()

    const params = new SearchParams(window.location.search)
    const utmReferrer = params.get("utm-link") || storeState.auth.utmLink

    api.users.me(
      utmReferrer,
      (user) => {
        websocketApi.connect()
        // After resuming a session we clear the link and challenge utms
        store.dispatch(actions.auth.setClearUtm(true))
        store.dispatch(actions.user.receiveUser(user))
        store.dispatch(actions.auth.finishGettingUser())
        const ideaViewPreference = getLocalStorageItem("ideaViewTypePreference")
        if (ideaViewPreference && ideaViewPreference !== "board") {
          store.dispatch(actions.challenges.setIdeaViewType(ideaViewPreference))
        }

        const searchParams = new SearchParams(window.location.search)
        const paramLang = searchParams.get("lang")
        if (paramLang) {
          i18n.changeLanguage(paramLang)
          if (user.language && user.language !== paramLang) {
            toast(
              (t) =>
                createElement(
                  "div",
                  {
                    style: {
                      fontSize: "0.9em",
                    },
                  },
                  [
                    "You followed a link with a different language preference than yours.",
                    createElement(
                      "div",
                      {
                        style: {
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "1rem",
                        },
                      },
                      [
                        createElement(
                          Button,
                          {
                            size: "small",
                            onClick: () => {
                              i18n.changeLanguage(user.language)
                              toast.dismiss(t.id)
                            },
                          },
                          "Revert",
                        ),
                        createElement(
                          Button,
                          {
                            size: "small",
                            primary: true,
                            onClick: () => {
                              toast.dismiss(t.id)
                            },
                          },
                          "Keep",
                        ),
                      ],
                    ),
                  ],
                ),
              {
                duration: Infinity,
              },
            )
          }
        } else if (user.language) {
          i18n.changeLanguage(user.language)
        } else {
          i18n.changeLanguage(user.ownerOrganisation?.defaultLanguage || "en")
        }
        Sentry.setUser({
          id: user._id,
          username: user.profile.fullName,
          organisation: user.ownerOrganisation.name,
          orgId: user.ownerOrganisation._id,
        })
        success?.()
      },
      (err) => {
        fail?.(err)
        store.dispatch(actions.auth.getUserFail(err))
      },
    )
  },
  onLogout: onLogout,
  onFailedRequest: (xhr, onFail) => {
    if (xhr.status === 530) {
      onFail?.({
        status: 530,
        message: "Access from this IP address is not allowed",
      })
      return
    } else if (xhr.status === 401) {
      onLogout()
      onFail?.({
        status: 401,
        message: "Session expired",
      })
      return
    }
    try {
      const response = JSON.parse(xhr.responseText)
      onFail?.({
        status: xhr.status,
        message: response.message || xhr.statusText,
      })
    } catch {
      onFail?.({
        status: xhr.status,
        message: xhr.statusText,
      })
    }
  },
})

export { asQuery, asMutation }
export default api
